import { type ReactNode } from 'react';
import { useAuth, type Permission } from '../../contexts/AuthContext';

// Re-export Permission type for convenience
export type { Permission };

interface PermissionGuardProps {
  children: ReactNode;
  /** Single permission to check */
  permission?: Permission;
  /** Multiple permissions to check */
  permissions?: Permission[];
  /** If true, all permissions are required; otherwise any one is sufficient */
  requireAll?: boolean;
  /** Content to render when permission check fails */
  fallback?: ReactNode;
}

/**
 * PermissionGuard - Conditionally render children based on user permissions
 *
 * Provides declarative permission-based UI rendering for admin components.
 * Works with the AuthContext to check user permissions.
 *
 * @example
 * // Single permission check
 * <PermissionGuard permission="MANAGE_THERAPISTS">
 *   <button>Create Therapist</button>
 * </PermissionGuard>
 *
 * @example
 * // Any of multiple permissions (OR logic)
 * <PermissionGuard permissions={['MANAGE_USERS', 'MANAGE_THERAPISTS']}>
 *   <AdminSection />
 * </PermissionGuard>
 *
 * @example
 * // All permissions required (AND logic)
 * <PermissionGuard permissions={['MANAGE_USERS', 'VIEW_AUDIT_LOGS']} requireAll>
 *   <SensitiveSection />
 * </PermissionGuard>
 *
 * @example
 * // With fallback content
 * <PermissionGuard permission="MANAGE_SETTINGS" fallback={<p>Access denied</p>}>
 *   <SettingsPanel />
 * </PermissionGuard>
 */
export function PermissionGuard({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, isAuthenticated, isAdmin } = useAuth();

  // Must be authenticated AND be an admin
  if (!isAuthenticated || !isAdmin()) {
    return <>{fallback}</>;
  }

  // Single permission check
  if (permission) {
    if (!hasPermission(permission)) {
      return <>{fallback}</>;
    }
    return <>{children}</>;
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      // All permissions required (AND logic)
      const hasAll = permissions.every((p) => hasPermission(p));
      if (!hasAll) {
        return <>{fallback}</>;
      }
    } else {
      // Any permission is sufficient (OR logic)
      if (!hasAnyPermission(permissions)) {
        return <>{fallback}</>;
      }
    }
    return <>{children}</>;
  }

  // No permission specified, render children if authenticated admin
  return <>{children}</>;
}

/**
 * Hook for imperative permission checks in components
 *
 * @example
 * function MyComponent() {
 *   const canManageUsers = usePermission('MANAGE_USERS');
 *
 *   return (
 *     <div>
 *       {canManageUsers && <button>Delete User</button>}
 *     </div>
 *   );
 * }
 */
export function usePermission(permission: Permission): boolean {
  const { hasPermission, isAuthenticated, isAdmin } = useAuth();
  return isAuthenticated && isAdmin() && hasPermission(permission);
}

/**
 * Hook for checking multiple permissions
 *
 * @example
 * function MyComponent() {
 *   const { hasAny, hasAll } = usePermissions(['MANAGE_USERS', 'VIEW_AUDIT_LOGS']);
 *
 *   return (
 *     <div>
 *       {hasAny && <AdminNav />}
 *       {hasAll && <SensitiveControls />}
 *     </div>
 *   );
 * }
 */
export function usePermissions(permissions: Permission[]): {
  hasAny: boolean;
  hasAll: boolean;
  check: (permission: Permission) => boolean;
} {
  const { hasPermission, hasAnyPermission, isAuthenticated, isAdmin } = useAuth();

  const isAdminUser = isAuthenticated && isAdmin();

  return {
    hasAny: isAdminUser && hasAnyPermission(permissions),
    hasAll: isAdminUser && permissions.every((p) => hasPermission(p)),
    check: (permission: Permission) => isAdminUser && hasPermission(permission),
  };
}
